import { useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { APP_TYPES, COMMON_TYPE } from '@scio3/shared-types';
import { PLAN_FEATURES_QUERY } from '@scio3/graphql-client';
import { getCurrencySymbol } from '@/features/billing/utils/plan-helpers';
import { optidevBasePrice } from '@/features/billing/utils/optidev-addon';

/** Engage per-device add-on. Not a COMMON_TYPE/APP_TYPES (engage content is a
 *  plain asset tagged `engage:true`), so it uses a literal type key. */
export const ENGAGE_ADDON_TYPE = 'engage' as const;

type AddOnType =
  | typeof APP_TYPES.BACKGROUND_MUSIC
  | typeof COMMON_TYPE.AERICAST
  | typeof COMMON_TYPE.VIDEO_WALL
  | typeof APP_TYPES.OPTIDEV
  | typeof ENGAGE_ADDON_TYPE;

/** Add-on key → plan feature key mapping (matches scio2 PLAN_FEATURE_ADD_ONS_KEY) */
export const ADD_ON_KEYS: Record<AddOnType, string> = {
  [APP_TYPES.BACKGROUND_MUSIC]: 'backgroundmusic',
  [COMMON_TYPE.AERICAST]: 'aericast',
  [COMMON_TYPE.VIDEO_WALL]: 'videowall',
  [APP_TYPES.OPTIDEV]: 'optidev',
  [ENGAGE_ADDON_TYPE]: 'engage',
};

/** Fallback prices if query fails or data is missing */
const FALLBACK_PRICES: Record<AddOnType, string> = {
  [APP_TYPES.BACKGROUND_MUSIC]: '$15',
  [COMMON_TYPE.AERICAST]: '$20',
  [COMMON_TYPE.VIDEO_WALL]: '$25',
  // Base package (3 licenses included), not a per-screen rate.
  [APP_TYPES.OPTIDEV]: '$30',
  [ENGAGE_ADDON_TYPE]: '$20',
};

interface AddOnPriceEntry {
  price?: Record<string, Record<string, { price?: number }>>;
  numberDayTrial?: number;
  numberScreenTrial?: number;
  isAutoChargeEndTrial?: boolean;
  helpLinkTrial?: string;
}

interface PlanFeatureNode {
  _id: string;
  alias: string;
  add_ons?: Record<string, AddOnPriceEntry>;
}

interface PlanFeaturesData {
  planFeatures: {
    page: {
      edges: Array<{ node: PlanFeatureNode }>;
    };
  };
}

interface PlanFeaturesVars {
  query: { alias: string };
}

/**
 * Fetches add-on pricing from the planFeatures query (public, cached).
 * Returns a function to get the formatted price string for an add-on type.
 *
 * Matches scio1/scio2 pattern: hardcoded USD currency for screens toolbar.
 * Path: plan.add_ons[key].price['usd']['monthly'].price
 */
export function useAddOnPricing() {
  const { data } = useQuery<PlanFeaturesData, PlanFeaturesVars>(PLAN_FEATURES_QUERY, {
    variables: { query: { alias: 'standard' } },
    fetchPolicy: 'cache-and-network',
  });

  const addOns = data?.planFeatures?.page?.edges?.[0]?.node?.add_ons;

  const getPrice = useCallback(
    (addOnType: AddOnType, tier?: string | null): string => {
      const key = ADD_ON_KEYS[addOnType];
      if (!key || !addOns?.[key]) return FALLBACK_PRICES[addOnType] ?? '';

      // OptiDev is a base package, not a per-screen rate. Derived from the
      // catalogue so a productPlans price change isn't left stale here.
      if (addOnType === APP_TYPES.OPTIDEV) {
        const perScreen = addOns[key].price?.usd?.monthly?.price;
        if (typeof perScreen === 'number') {
          return `$${optidevBasePrice(undefined, perScreen).toFixed(0)}`;
        }
        return FALLBACK_PRICES[addOnType] ?? '';
      }

      // #1205 — tier-aware price for multi-tier addons (e.g. BackgroundMusic Pro = $25).
      // Mirrors `getPriceByKey`'s `${tier}_monthly` pattern so the toolbar dropdown
      // reflects what the user actually pays after upgrading the addon tier.
      const priceKey = tier && tier !== 'standard' ? `${tier}_monthly` : 'monthly';
      const tieredPrice = addOns[key].price?.usd?.[priceKey]?.price;
      if (typeof tieredPrice === 'number') {
        return `$${tieredPrice}`;
      }
      // Fallback to monthly when the requested tier doesn't have a configured price.
      const monthlyPrice = addOns[key].price?.usd?.monthly?.price;
      if (typeof monthlyPrice === 'number') {
        return `$${monthlyPrice}`;
      }
      return FALLBACK_PRICES[addOnType] ?? '';
    },
    [addOns],
  );

  /**
   * Same lookup as `getPrice` but takes the backend/storage form of the addon
   * key directly (`'aericast'`, `'videowall'`, `'backgroundmusic'`) — useful for
   * callers that only have the raw key (e.g. AddonRequiredDialog via the
   * openAddOnRequired error payload).
   */
  /**
   * Tier- and currency-aware price lookup for an add-on key.
   * Matches scio1's `priceKey = ${tier}_monthly` for non-standard tiers
   * (aericastAssignModal.js:77-79). Falls back to USD monthly when tier or
   * currency lookup misses.
   */
  const getPriceByKey = useCallback(
    (addOnKey: string, opts?: { tier?: string; currency?: string }): string => {
      const addon = addOns?.[addOnKey];
      const currency = (opts?.currency ?? 'usd').toLowerCase();
      const symbol = getCurrencySymbol(currency);
      const tier = opts?.tier && opts.tier !== 'standard' ? opts.tier : null;
      const priceKey = tier ? `${tier}_monthly` : 'monthly';
      const tierPrice = addon?.price?.[currency]?.[priceKey]?.price;
      if (typeof tierPrice === 'number') return `${symbol}${tierPrice}`;
      // Fallback: USD monthly (covers missing currency or missing tier entry)
      const usdMonthly = addon?.price?.usd?.monthly?.price;
      if (typeof usdMonthly === 'number') return `$${usdMonthly}`;
      const entry = (Object.entries(ADD_ON_KEYS) as Array<[AddOnType, string]>).find(
        ([, v]) => v === addOnKey,
      );
      return entry ? (FALLBACK_PRICES[entry[0]] ?? '') : '';
    },
    [addOns],
  );

  /**
   * Trial duration (days) for an add-on, read from planFeatures.
   * Matches scio1 fallback `add_on.numberDayTrial || 30` (aericastAssignModal.html:20-21).
   */
  const getDaysByKey = useCallback(
    (addOnKey: string): number => {
      const days = addOns?.[addOnKey]?.numberDayTrial;
      return typeof days === 'number' && days > 0 ? days : 30;
    },
    [addOns],
  );

  /**
   * Whether the addon auto-charges at trial end. Drives the V3/V5 vs V2 trial
   * pitch variant (scio1 aericastAssignModal.html:20-21).
   */
  const getIsAutoChargeEndTrialByKey = useCallback(
    (addOnKey: string): boolean => !!addOns?.[addOnKey]?.isAutoChargeEndTrial,
    [addOns],
  );

  /** Optional help link rendered inside the V3 trial pitch (scio1 .html:21). */
  const getHelpLinkTrialByKey = useCallback(
    (addOnKey: string): string | undefined => addOns?.[addOnKey]?.helpLinkTrial,
    [addOns],
  );

  /** Number of screens included with a trial (used by OptiDev base-plan copy). */
  const getNumberScreenTrialByKey = useCallback(
    (addOnKey: string): number => addOns?.[addOnKey]?.numberScreenTrial ?? 3,
    [addOns],
  );

  return {
    getPrice,
    getPriceByKey,
    getDaysByKey,
    getIsAutoChargeEndTrialByKey,
    getHelpLinkTrialByKey,
    getNumberScreenTrialByKey,
  };
}
